/**
 * The sponsor's upload link, on the sponsor detail screen.
 *
 * WHY AN EXISTING LINK CAN NEVER BE SHOWN, which is the obvious thing to
 * expect: it was never stored. Only a SHA-256 of the token goes in the
 * database, so a leaked backup, an over-broad support query or a compromised
 * read replica yields nothing usable. That is the entire security model for an
 * audience with no accounts and no passwords, and it is one-way by
 * construction.
 *
 * So "send it to them again" is always ISSUING A NEW ONE. The old link is
 * revoked in the same transaction, so there is never more than one that works
 * and a link forwarded too far stops working the moment a replacement goes
 * out. For the person sending it this is the same action with a better failure
 * mode.
 *
 * A new link can go out two ways, and both are deliberate. Emailing it is the
 * default and the link never reaches this screen. Asking to be shown it
 * instead exists for the case that actually recurs — the sponsor says the
 * email never arrived — where issuing another link that also just gets emailed
 * is a loop rather than a fix. It is a live credential, so it comes back to a
 * browser only when someone has explicitly asked.
 *
 * The panel explains this in place, because staff will look for a reveal
 * button and should find the reason it does not exist rather than assume the
 * feature is missing.
 */

import { T } from '../lib/copyEdit.tsx';
import { useState } from 'react';

import type { LinkState } from '../pages/SponsorDetail';

export default function SponsorLinkPanel({
  base,
  companyName,
  link,
  onIssued,
}: {
  base: string;
  companyName: string;
  link: LinkState | null;
  onIssued: () => void;
}) {
  /* The link, when staff asked for it back. Null while emailing. */
  const [issued, setIssued] = useState<string | null>(null);
  /* Who it was emailed to, so the confirmation names a person. */
  const [emailedTo, setEmailedTo] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function issue(deliver: 'email' | 'manual') {
    setWorking(true);
    setProblem(null);

    try {
      const res = await fetch(`${base}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deliver }),
      });
      if (!res.ok) throw new Error(String(res.status));

      const body = (await res.json()) as {
        portalUrl: string | null;
        emailed: boolean;
        sentTo: string | null;
      };

      /* Set before anything else can throw. When the link comes back this is
       * the only moment it exists in the browser; losing it here means
       * issuing another. */
      setIssued(body.portalUrl);
      setEmailedTo(body.emailed ? body.sentTo : null);
      setConfirming(false);
      onIssued();
    } catch {
      setProblem('Could not issue a link. Nothing was changed.');
    } finally {
      setWorking(false);
    }
  }

  async function copy() {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued);
      setCopied(true);
    } catch {
      /* Clipboard access can be refused by the browser. The input is readonly
       * and selectable, so the link is still reachable by hand. */
      setProblem('Could not copy. Select the link and copy it manually.');
    }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h3><T k="sponsorLink.title" d="Upload Link" /></h3>
        <span className="hint">{describe(link)}</span>
      </div>

      <div className="panel-body">
        <p className="fdesc" style={{ marginTop: 0 }}>
          {companyName} uploads their logo through a single link. It needs no account and no
          password, works until the deadline plus the event's grace window, and can only ever
          upload to this one event.
        </p>

        {emailedTo ? (
          <div className="notice notice--action" style={{ marginBottom: 0 }}>
            <b>A new link is on its way to {emailedTo}</b>
            <p className="fdesc" style={{ margin: '4px 0 8px' }}>
              Any earlier link has stopped working. The link itself is not shown here, because only
              a fingerprint of it is stored.
            </p>
            <button
              type="button"
              className="btn-compact btn-compact--quiet"
              onClick={() => setEmailedTo(null)}
            >
              Done
            </button>
          </div>
        ) : issued !== null ? (
          <div className="notice notice--action" style={{ marginBottom: 0 }}>
            <b>Send this to {companyName}</b>
            <p className="fdesc" style={{ margin: '4px 0 8px' }}>
              Shown once and never again, because only a fingerprint of it is stored. Any earlier
              link has stopped working.
            </p>
            <div className="handoff">
              <input
                className="fi"
                readOnly
                value={issued}
                aria-label="Upload link"
                onFocus={(e) => e.target.select()}
              />
              <button type="button" className="btn-compact btn-compact--quiet" onClick={() => void copy()}>
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button
                type="button"
                className="btn-compact btn-compact--quiet"
                onClick={() => {
                  setIssued(null);
                  setCopied(false);
                }}
              >
                Done
              </button>
            </div>
            {problem && <p className="auth-error">{problem}</p>}
          </div>
        ) : confirming ? (
          <>
            <p className="fwarn">
              {link && !link.expired
                ? 'Their current link stops working immediately, including any copy they have already been sent or forwarded. Anything they have uploaded is kept.'
                : 'They get a fresh link for this event.'}
            </p>
            <div className="factions factions--stack">
              <button
                type="button"
                className="btn-compact"
                disabled={working}
                onClick={() => void issue('email')}
              >
                {working ? 'Working…' : 'Create and Email It'}
              </button>
              {/* The escape hatch for "they say it never arrived". Issuing
                  another link that also just gets emailed is a loop, not a
                  fix. */}
              <button
                type="button"
                className="btn-compact btn-compact--quiet"
                disabled={working}
                onClick={() => void issue('manual')}
              >
                Create and Show Me the Link
              </button>
              <button
                type="button"
                className="btn-compact btn-compact--quiet"
                onClick={() => setConfirming(false)}
                disabled={working}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            {/* The reason there is no reveal button. Staff will look for one. */}
            <p className="fdesc">
              An existing link can never be shown again, because only a fingerprint of it is
              stored. To get one to a sponsor, create a new one. It can be emailed to them
              directly, or shown to you once if you need to pass it on yourself. Either way the old
              link stops working, so there is only ever one that opens.
            </p>
            {problem && <p className="auth-error">{problem}</p>}
            <button type="button" className="btn-compact" onClick={() => setConfirming(true)}>
              {link && !link.expired ? 'Create a New Link' : 'Create a Link'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** The panel-head hint: enough to answer "does this sponsor have a working
 *  link?" without implying the link itself is retrievable. */
function describe(link: LinkState | null): string {
  if (!link) return 'none issued';
  if (link.expired) return 'expired';
  const lapses = new Date(link.expiresAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return link.lastUsedAt ? `opened · until ${lapses}` : `not opened yet · until ${lapses}`;
}
